import React, { useCallback, useContext, useState } from 'react';
import { Button, Collapse } from 'reactstrap';
import { AppContext } from '../context';
import NoDataFound from './layout/NoDataFound';
import TableComponent from './layout/TableComponent';
import DELETE_ICON from '../assets/images/delete-icon-menu.svg';
import VIEW_ICON from '../assets/images/view-icon.svg';

const NOTES_STORAGE_KEY = 'notes';

const DRAFT_NOTES_TABLE_HEADER = [
  'Notes Type',
  'Category',
  'Notes',
  'Last Seen',
  'Actions',
];

export interface LocalStorageNote {
  user?: string;
  CandidateName?: string;
  category?: string;
  URL?: string;
  notes: string;
  savedAt?: string;
  noteType?: string;
}

export const getNotesFromStorage = (
  userId: string | undefined
): { notes: LocalStorageNote[]; fullList: LocalStorageNote[] } => {
  if (!userId) return { notes: [], fullList: [] };
  try {
    const raw = localStorage.getItem(NOTES_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    const fullList = Array.isArray(parsed) ? parsed : [parsed];
    const notes = fullList.filter(
      (n: LocalStorageNote) => n?.user === userId
    ) as LocalStorageNote[];
    return { notes, fullList: fullList as LocalStorageNote[] };
  } catch {
    return { notes: [], fullList: [] };
  }
};

const formatLastSeen = (savedAt: string | undefined): string => {
  if (!savedAt) return '—';
  try {
    const d = new Date(savedAt);
    if (Number.isNaN(d.getTime())) return savedAt;
    return d.toLocaleString();
  } catch {
    return savedAt || '—';
  }
};

/** Strip HTML tags and return plain text. */
const notesToPlainText = (html: string | undefined): string => {
  if (!html) return '—';
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!text) return '—';
  const words = text.split(' ');
  if (words.length <= 3) return text;
  return words.slice(0, 3).join(' ') + '...';
};

/** Strip HTML tags but preserve line breaks and tabs for display with whiteSpace: pre-wrap. */
const fullNotesToPlainText = (html: string | undefined): string => {
  if (!html) return '—';
  let text = html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<p(?:\s[^>]*)?>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<div(?:\s[^>]*)?>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"');
  text = text.replace(/\n+/g, '\n').trim();
  return text || '—';
};


interface LocalStorageNotesTableProps {
  onNotesChange?: (hasNotes: boolean) => void;
}

const LocalStorageNotesTable = ({ onNotesChange }: LocalStorageNotesTableProps) => {
  const { user } = useContext(AppContext);
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshNotes = useCallback(() => setRefreshKey((k) => k + 1), []);

  const { notes: userNotes, fullList } = getNotesFromStorage(user?.id);

  React.useEffect(() => {
    onNotesChange?.(userNotes.length > 0);
  }, [userNotes.length, onNotesChange]);

  const [expandedNoteIndex, setExpandedNoteIndex] = useState<number | null>(null);

  const toggleExpand = (index: number) => {
    setExpandedNoteIndex((prev) => (prev === index ? null : index));
  };

  const handleDelete = (fullListIndex: number) => {
    try {
      const raw = localStorage.getItem(NOTES_STORAGE_KEY);
      let list: LocalStorageNote[] = raw ? JSON.parse(raw) : [];
      list = Array.isArray(list) ? list : [list];
      const filtered = list.filter(
        (_: LocalStorageNote, i: number) => i !== fullListIndex
      );
      localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(filtered));
      refreshNotes();
    } catch {
      refreshNotes();
    }
  };

  const userIndices: number[] = [];
  fullList.forEach((n: LocalStorageNote, i: number) => {
    if (n?.user === user?.id) userIndices.push(i);
  });

  return (
    <div className="table-responsive">
      <TableComponent tableHeader={DRAFT_NOTES_TABLE_HEADER}>
        {!userNotes?.length ? (
          <tbody>
            <tr>
              <td colSpan={5} className="empty-table-td">
                <NoDataFound text="No Draft Notes Exists" />
              </td>
            </tr>
          </tbody>
        ) : (
          <tbody>
          {userNotes.map((note, index) => {
            const fullListIndex = userIndices[index];
            const isExpanded = expandedNoteIndex === index;
            return (
              <React.Fragment
                key={`${note.URL}-${index}-${note.savedAt || ''}-${refreshKey}`}
              >
                <tr>
                  <td>{note.noteType ?? '—'}</td>
                  <td>{note.category ?? '--'}</td>
                  <td>
                    <div className="local-storage-note-content">
                      {notesToPlainText(note.notes)}
                    </div>
                  </td>
                  <td>{formatLastSeen(note.savedAt)}</td>
                  <td>
                    <div className="d-flex align-items-center">
                      <Button
                        color="link"
                        size="sm"
                        className="p-0 mr-2"
                        onClick={() => toggleExpand(index)}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Collapse note' : 'View full note'}
                      >
                        <img src={VIEW_ICON} alt="View" />
                      </Button>
                      <Button
                        color="link"
                        size="sm"
                        className="text-danger p-0"
                        onClick={() =>
                          fullListIndex >= 0 &&
                          handleDelete(fullListIndex)
                        }
                      >
                        <img src={DELETE_ICON} alt="Delete" />
                      </Button>
                    </div>
                  </td>
                </tr>
                <tr
                  className={`draft-notes-collapse-row${!isExpanded ? ' collapsed' : ''}`}
                >
                  <td colSpan={5} className="p-0 border-0">
                    <Collapse isOpen={isExpanded}>
                      <div
                        className="p-3 for-wordfull-break bg-light"
                        style={{ whiteSpace: 'pre-wrap' }}
                      >
                        {fullNotesToPlainText(note.notes)}
                      </div>
                    </Collapse>
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
          </tbody>
        )}
      </TableComponent>
    </div>
  );
};

export default LocalStorageNotesTable;
